import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, IconButton, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';

import { useIsMobile } from '@izimi/hooks';
import type { DocumentEntity } from '@izimi/modules';

import { HistoryLogList } from '../../historyLog';
import { CustomIcon, FeatherIcon } from '../../icon';
import SideBar from '../../SideBar/SideBar';

type Props = {
  document: DocumentEntity;
  isVisible: boolean;
  handleEditDocumentName?: (document: DocumentEntity) => void;
  setIsVisible: (isVisible: boolean) => void;
  children?: ReactNode;
};

export const DocumentPreviewSideBar = ({
  document,
  isVisible,
  handleEditDocumentName,
  setIsVisible,
  children,
}: Props) => {
  const { t } = useTranslation('shared');
  const isMobile = useIsMobile();

  return (
    <SideBar
      isVisible={isVisible}
      title={document.fileNameWithoutExtension}
      onClose={() => setIsVisible(false)}
      // 81px header height - padding - 1px divider
      HeaderRight={
        <Flex height='55px'>
          {document.isOwner && handleEditDocumentName && (
            <IconButton
              aria-label={t('action.edit')}
              icon={<FeatherIcon name='edit-2' size={20} color='neutral500' />}
              onClick={() => handleEditDocumentName(document)}
            />
          )}
          {isMobile && (
            <IconButton
              aria-label={t('action.close')}
              icon={<CustomIcon name='slide-out' color='neutral500' />}
              onClick={() => setIsVisible(false)}
            />
          )}
        </Flex>
      }>
      {children}
    </SideBar>
  );
};

const SideBarTabs = ({
  renderAccordion,
  document,
}: {
  renderAccordion: ReactNode;
  document: DocumentEntity;
}) => {
  const { t } = useTranslation('shared');

  return (
    <Tabs flex={1} display='flex' flexDir='column'>
      <TabList pos='sticky' top='88px' zIndex='sticky' bg='white'>
        <Tab>{t('navigation.document')}</Tab>
        <Tab>{t('navigation.history')}</Tab>
      </TabList>
      <TabPanels flex={1} display='flex'>
        <TabPanel flex={1} display='flex'>
          {renderAccordion}
        </TabPanel>
        <TabPanel>
          <HistoryLogList documentId={document.id} useBodyScroll={false} />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

DocumentPreviewSideBar.Tabs = SideBarTabs;
